import {ApplicationRef, createComponent, effect, EnvironmentInjector} from '@angular/core';
import * as L from 'leaflet';
import {Tile} from '@shared/models/tile';
import {CanvasMiniTileComponent} from '@features/canvas-tiles/editor/components/canvas-mini-tile/canvas-mini-tile.component';
import {CanvasClusterLayer} from './canvas-map-controls';
import {CanvasMarkerEntry, CanvasMarkerOptions} from './canvas-marker.models';
import {sameTileIds} from './canvas-point-utils';

export class CanvasMarkerRegistry {
  private readonly entries = new Map<string, CanvasMarkerEntry>();
  private readonly markerIdByMarker = new Map<L.Marker, string>();
  private nextUid = 0;

  constructor(
    private readonly appRef: ApplicationRef,
    private readonly injector: EnvironmentInjector,
    private readonly getClusterLayer: () => CanvasClusterLayer | undefined,
  ) {}

  nextId(): string {
    return `marker-${this.nextUid++}`;
  }

  get(markerId: string): CanvasMarkerEntry | undefined {
    return this.entries.get(markerId);
  }

  markerIds(): string[] {
    return [...this.entries.keys()];
  }

  markers(): L.Marker[] {
    return [...this.entries.values()].map(entry => entry.marker);
  }

  forEach(callback: (entry: CanvasMarkerEntry, markerId: string) => void): void {
    this.entries.forEach(callback);
  }

  tilesOf(markers: L.Marker[]): Tile[] {
    return markers.flatMap(marker => {
      const entry = this.entries.get(this.markerIdByMarker.get(marker) ?? '');
      return entry?.componentRef.instance.tilesData.map(data => data.tile).filter(Boolean) ?? [];
    });
  }

  add(markerId: string, options: CanvasMarkerOptions): CanvasMarkerEntry {
    const componentRef = createComponent(CanvasMiniTileComponent, {environmentInjector: this.injector});
    componentRef.instance.tileIds = options.tileIds;
    componentRef.instance.shouldPoll = options.shouldPoll;
    if (options.replayResult$) {
      componentRef.instance.replayResult$ = options.replayResult$;
    }
    componentRef.instance.clicked.subscribe(options.onClick);
    this.appRef.attachView(componentRef.hostView);
    componentRef.changeDetectorRef.detectChanges();

    const marker = L.marker(options.position, {
      icon: L.divIcon({
        className: 'canvas-mini-tile-marker',
        html: componentRef.location.nativeElement as HTMLElement,
        iconSize: [0, 0],
      }),
      interactive: true,
      draggable: options.draggable ?? false,
    });

    (marker as any)._tileColors = componentRef.instance.colors();
    (marker as any)._tileAlarmBadge = componentRef.instance.alarmBadge();
    const colorEffect = effect(() => {
      (marker as any)._tileColors = componentRef.instance.colors();
      (marker as any)._tileAlarmBadge = componentRef.instance.alarmBadge();
      this.getClusterLayer()?.refreshMarker(marker);
    }, {injector: this.injector});

    const entry = {marker, tileIds: options.tileIds, componentRef, colorEffect};
    this.entries.set(markerId, entry);
    this.markerIdByMarker.set(marker, markerId);
    return entry;
  }

  reconcile(desired: {markerId: string; options: CanvasMarkerOptions}[]): L.Marker[] {
    const wanted = new Set(desired.map(item => item.markerId));
    this.markerIds().filter(markerId => !wanted.has(markerId)).forEach(markerId => this.remove(markerId));

    return desired.map(({markerId, options}) => {
      const existing = this.entries.get(markerId);
      if (existing && sameTileIds(existing.tileIds, options.tileIds)) return existing.marker;

      this.remove(markerId);
      return this.add(markerId, options).marker;
    });
  }

  remove(markerId: string): L.Marker | undefined {
    const entry = this.entries.get(markerId);
    if (!entry) return undefined;

    entry.colorEffect.destroy();
    this.appRef.detachView(entry.componentRef.hostView);
    entry.componentRef.destroy();
    this.entries.delete(markerId);
    this.markerIdByMarker.delete(entry.marker);
    return entry.marker;
  }

  clear(): void {
    this.markerIds().forEach(markerId => this.remove(markerId));
  }
}
